import { z } from 'zod'
import type { DashboardPluginKey } from '../../enums/dashboard-plugin-key.enum'

export const currentExpensesConfigSchema = z.object({
  period: z.enum(['month', 'week', 'quarter', 'year']).default('month'),
  limit: z.number().int().min(1).max(50).default(5),
})
export type CurrentExpensesConfig = z.infer<typeof currentExpensesConfigSchema>

export const currentIncomesConfigSchema = z.object({
  period: z.enum(['month', 'week', 'quarter', 'year']).default('month'),
  limit: z.number().int().min(1).max(50).default(5),
})
export type CurrentIncomesConfig = z.infer<typeof currentIncomesConfigSchema>

export const incomeExpenseChartConfigSchema = z.object({
  historyMonths: z.number().int().min(1).max(60).default(12),
  projectionMonths: z.number().int().min(1).max(60).default(12),
  colors: z
    .object({
      active: z.string().default('#ef4444'),
      passive: z.string().default('#22c55e'),
      expense: z.string().default('#3b82f6'),
    })
    .prefault({}),
})
export type IncomeExpenseChartConfig = z.infer<typeof incomeExpenseChartConfigSchema>

export const monthsLeftConfigSchema = z.object({
  accountIds: z.array(z.string().guid()).nullable().default(null),
  historyMonths: z.number().int().min(1).max(60).default(6),
  projectionMonths: z.number().int().min(1).max(60).default(12),
  excludeActiveIncome: z.boolean().default(false),
})
export type MonthsLeftConfig = z.infer<typeof monthsLeftConfigSchema>

export const incomeExpenseTableConfigSchema = z.object({
  dateRange: z
    .object({
      start: z.string().datetime({ offset: true }),
      end: z.string().datetime({ offset: true }),
    })
    .nullable()
    .default(null),
})
export type IncomeExpenseTableConfig = z.infer<typeof incomeExpenseTableConfigSchema>

export const currencyPairsConfigSchema = z.object({
  pairs: z
    .array(
      z.object({
        from: z.string().length(3),
        to: z.string().length(3),
      })
    )
    .max(20)
    .default([]),
  changePeriodDays: z.number().int().min(1).max(365).default(30),
  showSparkline: z.boolean().default(true),
})
export type CurrencyPairsConfig = z.infer<typeof currencyPairsConfigSchema>

// how many instrument rows a broker-account widget renders. exported so the settings form clamps
// to the same bounds the schema enforces instead of hardcoding its own copy.
export const BROKER_WIDGET_MIN_ITEMS = 3
export const BROKER_WIDGET_MAX_ITEMS = 10
export const BROKER_WIDGET_DEFAULT_ITEMS = 5

// a T-Bank broker account id: short and opaque, and interpolated into request paths by every
// consumer, so it is bounded to the charset and length the backend's ShortIdPipe accepts
const brokerAccountIdSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[A-Za-z0-9_-]+$/)
  .nullable()
  .default(null)

// the config every widget that watches a single broker account shares: which connection and
// account to read, and how many instrument rows to show. ids are null until configured.
export const brokerAccountWidgetConfigSchema = z.object({
  connectionId: z.string().uuid().nullable().default(null),
  accountId: brokerAccountIdSchema,
  itemCount: z
    .number()
    .int()
    .min(BROKER_WIDGET_MIN_ITEMS)
    .max(BROKER_WIDGET_MAX_ITEMS)
    .default(BROKER_WIDGET_DEFAULT_ITEMS),
})
export type BrokerAccountWidgetConfig = z.infer<typeof brokerAccountWidgetConfigSchema>

// bot-monitor and day-trade-monitor differ in what they render, not in how they are pointed at an
// account, so both are the shared shape under their own name (the registry keys them separately)
export const botMonitorConfigSchema = brokerAccountWidgetConfigSchema
export type BotMonitorConfig = BrokerAccountWidgetConfig

export const dayTradeMonitorConfigSchema = brokerAccountWidgetConfigSchema
export type DayTradeMonitorConfig = BrokerAccountWidgetConfig

// one history-index widget resolves its data through a picked broker account's connection
// (read-only token); chartRange picks the compact price chart window. ids are null until configured.
export const historyIndexConfigSchema = z.object({
  connectionId: z.string().uuid().nullable().default(null),
  accountId: brokerAccountIdSchema,
  chartRange: z.enum(['week', 'month']).default('week'),
})
export type HistoryIndexConfig = z.infer<typeof historyIndexConfigSchema>

// the config schema for every plugin key, so a stored config is validated the same way wherever it
// is read: the backend parses against it on write, and the frontend registry renders from it.
export const dashboardPluginConfigSchemas: Record<DashboardPluginKey, z.ZodType> = {
  'current-expenses': currentExpensesConfigSchema,
  'current-incomes': currentIncomesConfigSchema,
  'income-expense-chart': incomeExpenseChartConfigSchema,
  'months-left': monthsLeftConfigSchema,
  'income-expense-table': incomeExpenseTableConfigSchema,
  'currency-pairs': currencyPairsConfigSchema,
  'bot-monitor': botMonitorConfigSchema,
  'history-index': historyIndexConfigSchema,
  'day-trade-monitor': dayTradeMonitorConfigSchema,
}
